"use client";

import { ChevronRight, LogOut } from "lucide-react";
import { type ComponentProps, type ReactNode, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/avatar";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../components/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../components/dropdown-menu";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	useSidebar,
} from "../components/sidebar";
import {
	deriveInitials,
	isNavActive,
	type NavItem,
	type NavLinkComponent,
	type NavSection,
	type ShellBrand,
	type ShellUser,
} from "./types";

export interface AppSidebarProps extends ComponentProps<typeof Sidebar> {
	navSections: NavSection[];
	pathname: string;
	LinkComponent: NavLinkComponent;
	brand: ShellBrand;
	user?: ShellUser;
	onLogout?: () => void;
	/** Replaces the default brand block in the sidebar header entirely. */
	headerSlot?: ReactNode;
	/** Replaces the default user footer entirely. */
	footer?: ReactNode;
	/**
	 * Small meta line rendered at the very bottom of the footer (e.g. app
	 * version). Hidden when the sidebar is collapsed to icons.
	 */
	footerMeta?: ReactNode;
	/** When true, items with `indicator: true` render a warning dot. */
	indicatorActive?: boolean;
}

export function AppSidebar({
	navSections,
	pathname,
	LinkComponent,
	brand,
	user,
	onLogout,
	headerSlot,
	footer,
	footerMeta,
	indicatorActive,
	...props
}: AppSidebarProps) {
	const Link = LinkComponent;
	return (
		<Sidebar collapsible="icon" {...props}>
			<SidebarHeader>
				{headerSlot ?? (
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton size="lg" asChild>
								<Link href={brand.href ?? "/"}>
									{brand.icon ? (
										<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
											{brand.icon}
										</div>
									) : null}
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">{brand.name}</span>
										{brand.subtitle ? (
											<span className="truncate text-xs text-muted-foreground">
												{brand.subtitle}
											</span>
										) : null}
									</div>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				)}
			</SidebarHeader>
			<SidebarContent>
				{navSections.map((section, sectionIndex) => {
					const visible = section.items.filter((item) => !item.hidden);
					if (visible.length === 0) return null;
					return (
						<SidebarGroup key={section.label ?? `section-${sectionIndex}`}>
							{section.label ? (
								<SidebarGroupLabel>{section.label}</SidebarGroupLabel>
							) : null}
							<SidebarMenu>
								{visible.map((item) => (
									<SidebarNavItem
										key={item.href}
										item={item}
										pathname={pathname}
										Link={Link}
										indicatorActive={indicatorActive}
									/>
								))}
							</SidebarMenu>
						</SidebarGroup>
					);
				})}
			</SidebarContent>
			<SidebarFooter>
				{footer ?? (user ? <NavUser user={user} onLogout={onLogout} /> : null)}
				{footerMeta ? (
					<div className="px-2 pb-1 text-center text-[10px] leading-none text-muted-foreground/70 group-data-[collapsible=icon]:hidden">
						{footerMeta}
					</div>
				) : null}
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}

function SidebarNavItem({
	item,
	pathname,
	Link,
	indicatorActive,
}: {
	item: NavItem;
	pathname: string;
	Link: NavLinkComponent;
	indicatorActive?: boolean;
}) {
	const subItems = (item.items ?? []).filter((sub) => !sub.hidden);
	// The parent highlights on its own href or any child. A parent with
	// children uses non-exact matching so /cluster stays lit on /cluster/pool.
	const active =
		isNavActive(pathname, item.href, item.exact) ||
		subItems.some((sub) => isNavActive(pathname, sub.href, sub.exact));
	const showDot = item.indicator && indicatorActive;
	const Icon = item.icon;

	// Collapsed by default when the section is inactive; the chevron lets
	// the user peek anytime. While a route inside the section is active the
	// sub list stays open so the current location is always visible.
	const [userOpen, setUserOpen] = useState(false);
	const open = userOpen || active;

	const parentButton = (
		<SidebarMenuButton asChild isActive={active} tooltip={item.label}>
			<Link href={item.href}>
				{Icon ? <Icon /> : null}
				<span>{item.label}</span>
			</Link>
		</SidebarMenuButton>
	);

	const dotBadge = showDot ? (
		<SidebarMenuBadge
			className={
				subItems.length > 0
					? // Leave room for the chevron action on collapsible items.
						"right-7 bg-amber-500/20 text-amber-700 dark:text-amber-300"
					: "bg-amber-500/20 text-amber-700 dark:text-amber-300"
			}
			aria-label="Unrendered changes"
			title="Unrendered changes"
		>
			●
		</SidebarMenuBadge>
	) : null;

	if (subItems.length === 0) {
		return (
			<SidebarMenuItem>
				{parentButton}
				{dotBadge}
			</SidebarMenuItem>
		);
	}

	return (
		<Collapsible asChild open={open} onOpenChange={setUserOpen}>
			<SidebarMenuItem>
				{parentButton}
				{dotBadge}
				<CollapsibleTrigger asChild>
					<SidebarMenuAction
						className="data-[state=open]:rotate-90"
						aria-label={`Toggle ${item.label} section`}
					>
						<ChevronRight />
					</SidebarMenuAction>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<SidebarMenuSub>
						{subItems.map((sub) => (
							<SidebarMenuSubItem key={sub.href}>
								<SidebarMenuSubButton
									asChild
									isActive={isNavActive(pathname, sub.href, sub.exact)}
								>
									<Link href={sub.href}>
										<span>{sub.label}</span>
									</Link>
								</SidebarMenuSubButton>
							</SidebarMenuSubItem>
						))}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	);
}

function NavUser({
	user,
	onLogout,
}: {
	user: ShellUser;
	onLogout?: () => void;
}) {
	const { isMobile } = useSidebar();
	const initials = user.initials ?? deriveInitials(user.name);
	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="size-8 rounded-md">
								{user.image ? (
									<AvatarImage src={user.image} alt={user.name} />
								) : null}
								<AvatarFallback className="rounded-md font-mono text-xs">
									{initials}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">{user.name}</span>
								{user.subtitle ? (
									<span className="truncate text-xs text-muted-foreground">
										{user.subtitle}
									</span>
								) : null}
							</div>
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="font-normal">
							<div className="text-sm font-medium">{user.name}</div>
							{user.subtitle ? (
								<div className="text-xs text-muted-foreground">
									{user.subtitle}
								</div>
							) : null}
						</DropdownMenuLabel>
						{onLogout ? (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => onLogout()}>
									<LogOut />
									Sign out
								</DropdownMenuItem>
							</>
						) : null}
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
